import "@mantine/core/styles.css";

import { Cloudinary } from "@cloudinary/url-gen";
import "@mantine/carousel/styles.css";
import "@mantine/charts/styles.css";
import {
  CSSVariablesResolver,
  Loader,
  MantineProvider,
  v8CssVariablesResolver,
  rem,
} from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import "@mantine/dates/styles.css";
import "@mantine/schedule/styles.css";
import "@mantine/dropzone/styles.css";
import { ModalsProvider } from "@mantine/modals";
import {
  Notifications,
  notifications,
  notificationsStore,
} from "@mantine/notifications";
import "@mantine/notifications/styles.css";
import { NavigationProgress } from "@mantine/nprogress";
import "@mantine/nprogress/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/tiptap/styles.css";
import { IconCheck, IconX } from "@tabler/icons-react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import "dayjs/locale/en";
import "dayjs/locale/fi";
import "dayjs/locale/fr";
import "dayjs/locale/nb";
import "dayjs/locale/sv";
import i18next from "i18next";
import posthog, { type CaptureResult } from "posthog-js";
import { StrictMode, Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { AuthProvider, logOut, useAuth } from "./AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import AppCrashScreen from "./components/ErrorBoundary/AppCrashScreen";
import PageContainer from "./components/PageContainer/PageContainer";
import ErrorComponent from "./components/RoutingErrors/ErrorComponent/ErrorComponent";
import LoadingScreen from "./features/Layout/LoadingScreen/LoadingScreen";
import { useCurrentLanguage } from "./helpers/language.helpers";
import { scrubSecretUrls } from "./helpers/posthog.helpers";
import {
  apiGet,
  NetworkError,
  networkErrorPrefix,
  unreachableErrorPrefix,
} from "./helpers/request.helpers";
import "./i18n";
import { routeTree } from "./routeTree.gen";
import "./styles.scss";
import errorNotificationClasses from "./theme/notification.error.module.scss";
import successNotificationClasses from "./theme/notification.success.module.scss";
import { SKILLSTER_THEME } from "./theme/theme";
import {
  DEMO_BLOCKED_REASON,
  isUnreachable,
  shouldRetryQuery,
  unreachableMutationMessageKey,
} from "./helpers/errors.helpers";
import useServerStore from "./store/server";

// Vite rethrows the preload error only when the event was not
// default-prevented, so preventDefault() must stay on the reload path: calling
// it on the give-up path would swallow the failure instead of letting it reach
// the error boundary.
let preloadFailureHandled = false;

window.addEventListener("vite:preloadError", (event) => {
  // One route preload can fail several chunks in the same tick. The first event
  // decides for the whole page load, so the rest are left alone.
  if (preloadFailureHandled) return;
  preloadFailureHandled = true;

  // Stale chunk after a deploy heals with one reload — but if index.html is
  // also stale (bfcache/proxy) an unguarded reload loops forever. Let the
  // second failure within 30s reach the error boundary instead.
  let lastReload = 0;
  try {
    lastReload = Number(sessionStorage.getItem("chunk-reload-at") ?? 0);
  } catch {}

  if (Number.isFinite(lastReload) && Date.now() - lastReload < 30_000) {
    posthog.captureException(event.payload, {
      chunkReload: "second failure within 30s",
    });
    return;
  }

  try {
    sessionStorage.setItem("chunk-reload-at", String(Date.now()));
  } catch {}
  event.preventDefault();
  window.location.reload();
});

// if (import.meta.env.VITE_IS_LOCAL_HOST !== "true") {
//   posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
//     api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
//   });
// }
// Two exceptions dominate the error feed without ever being actionable:
// /get-session-user answering 401 is just an expired session — the query cache
// already logs the user out — and the ResizeObserver loop notice is a browser
// warning from the admin tables that changes nothing for the user. Matching on
// the message keeps the noise off the feed and out of the event quota; every
// other 401 still reports, since one on a different endpoint is a real bug.
// Backend outages are shown to the user by the error views and would otherwise flood
// the feed with one event per failed request.
const IGNORED_EXCEPTIONS = [
  networkErrorPrefix("/get-session-user", 401),
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  unreachableErrorPrefix(""),
];

function isIgnoredException(event: CaptureResult) {
  if (event.event !== "$exception") return false;

  const exceptions = event.properties?.$exception_list;
  if (!Array.isArray(exceptions)) return false;

  return exceptions.some((exception) =>
    IGNORED_EXCEPTIONS.some((ignored) =>
      `${exception?.type ?? ""}: ${exception?.value ?? ""}`.includes(ignored),
    ),
  );
}

// Browser page translation (Chrome, Edge) swaps React's text nodes for its own
// <font> wrappers, so React's next removeChild throws "NotFoundError: ... not a
// child of this node". Tagging exceptions lets Error Tracking tell those apart
// from real bugs.
function isPageTranslated() {
  const html = document.documentElement;
  return (
    html.classList.contains("translated-ltr") ||
    html.classList.contains("translated-rtl") ||
    document.querySelector("[_msttexthash]") !== null
  );
}

// Compared inline, not via a shared constant, so Vite folds it to a literal and
// the demo module is dropped from every other build.
const isDemo = import.meta.env.VITE_DEMO === "true";

// The demo reports to its own PostHog project, so its visits never pollute
// product analytics; without VITE_DEMO_POSTHOG_KEY it sends nothing.
const posthogKey = isDemo
  ? import.meta.env.VITE_DEMO_POSTHOG_KEY
  : import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    capture_pageview: false,
    capture_pageleave: true,
    capture_exceptions: true,
    // We don't use replay; pin it off so project settings can't start recording tokens in URLs.
    disable_session_recording: true,
    before_send: (event) => {
      if (!event || isIgnoredException(event)) return null;
      if (event.event === "$exception")
        event.properties.pageTranslated = isPageTranslated();
      return scrubSecretUrls(event);
    },
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export const router = createRouter({
  scrollRestoration: true,
  routeTree,
  defaultErrorComponent: ({ error }) => (
    <PageContainer>
      <ErrorComponent error={error} />
    </PageContainer>
  ),
  defaultPendingComponent: () => (
    <PageContainer>
      <Loader color="blue.5" />
    </PageContainer>
  ),
  // The component stack is the only pointer to app code for errors thrown in
  // React's commit phase, whose JS stack holds nothing but React internals.
  defaultOnCatch: (error, errorInfo) =>
    posthog.captureException(error, {
      componentStack: errorInfo?.componentStack,
    }),
  context: {
    auth: undefined!,
    queryClient: undefined!,
  },
});

router.subscribe("onResolved", ({ toLocation }) => {
  posthog.capture("$pageview", {
    $current_url: window.location.origin + toLocation.href,
    $pathname: toLocation.pathname,
  });
});

function InnerApp() {
  const auth = useAuth();
  const language = useCurrentLanguage();

  useEffect(() => {
    if (window.location.pathname === "/activate") {
      return;
    }
    // Kör en session-validering direkt, en gång
    // The 401 path is the expected one for an expired session and is handled
    // by queryCache.onError below; without a catch it surfaces as an unhandled
    // rejection instead. Everything else is a real failure of the session
    // endpoint, and queryCache.onError stays silent for it, so report it here —
    // swallowing the whole rejection would make a backend outage invisible.
    queryClient
      .fetchQuery({
        queryKey: ["get-session-user"],
        queryFn: async () => await apiGet("/get-session-user"),
        retry: false,
      })
      .catch((error) => {
        if (error instanceof NetworkError && error.StatusCode === 401) return;
        // The error views already cover an outage; QueryCache.onError raised the flag.
        if (isUnreachable(error)) return;
        posthog.captureException(error);
      });
  }, [queryClient]);

  return (
    <DatesProvider
      settings={{
        locale: language.dayjsLocale,
        consistentWeeks: true,
      }}
    >
      <RouterProvider router={router} context={{ auth, queryClient }} />
    </DatesProvider>
  );
}

export const cld = new Cloudinary({
  cloud: {
    cloudName: "dy7i8hekz",
  },
});

let previousLng: string | undefined;
i18next.on("languageChanged", (lng) => {
  document.documentElement.setAttribute("lang", lng);
  if (previousLng && previousLng !== lng) queryClient.invalidateQueries();
  previousLng = lng;
});

const xIcon = <IconX style={{ width: rem(20), height: rem(20) }} />;
const checkIcon = <IconCheck style={{ width: rem(20), height: rem(20) }} />;

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(error, query) {
      if (error instanceof NetworkError && error.StatusCode === 401) {
        logOut();
      }
      if (isUnreachable(error)) {
        useServerStore.getState().setUnreachable(true);
      }
    },
    onSuccess(data, query) {
      if (query.meta?.refetchSimulatorsHardware) {
        queryClient.refetchQueries({ queryKey: ["get-simulators-hardware"] });
      }
      if (query.meta?.invalidateExercises) {
        queryClient.invalidateQueries({ queryKey: ["exercises"] });
      }
    },
  }),
  // The default "online" networkMode pauses fetches after the browser offline
  // event (endless loaders, no error view, paused mutations replayed later; offline
  // queueing was rejected in CONTEXT). "always" lets apiFetch throw
  // UnreachableError into the error views and the recovery path.
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      networkMode: "always",
    },
    mutations: {
      networkMode: "always",
    },
  },
  mutationCache: new MutationCache({
    onSuccess(data, variables, context, mutation) {
      const successMessage = mutation.meta?.successMessage as
        | { title: string; message: string }
        | undefined;

      const autoClose = mutation.meta?.autoClose as
        | boolean
        | number
        | undefined;

      if (successMessage) {
        const duplicate = notificationsStore
          .getState()
          .notifications.find(
            (n) =>
              n.title === successMessage.title &&
              typeof n.message === "string" &&
              removeCountFromMessage(n.message) === successMessage.message,
          );
        if (duplicate && typeof duplicate.message === "string") {
          notifications.update({
            ...duplicate,
            message: updateMessageCount(duplicate.message),
          });
        } else {
          notifications.show({
            title: successMessage?.title,
            message: successMessage?.message,
            position: "bottom-center",
            classNames: successNotificationClasses,
            icon: checkIcon,
            color: "bluegray.9",
            autoClose,
          });
        }
      }
    },
    onError(error: any, variables, context, mutation) {
      if (error?.Response?.Reason === DEMO_BLOCKED_REASON) {
        notifications.show({
          id: "demo-blocked",
          title: i18next.t("demo.blocked.title"),
          message: i18next.t("demo.blocked.message"),
          position: "bottom-center",
        });
        return;
      }
      if (isUnreachable(error)) {
        useServerStore.getState().setUnreachable(true);
      }

      // message may be a function of the error body for reasons that carry data
      const errorMessages = mutation.meta?.errorMessages as
        | Record<
            string,
            { title?: string; message?: string | ((response: any) => string) }
          >
        | undefined;

      const statusCodesToIgnoreGlobally = mutation.meta
        ?.statusCodesToIgnoreGlobally as number[] | undefined;

      const reasonsToIgnoreGlobally = mutation.meta?.reasonsToIgnoreGlobally as
        | string[]
        | undefined;

      const errorMessage =
        errorMessages?.[error.Response?.Reason] ??
        errorMessages?.[error.StatusCode] ??
        {};

      if (statusCodesToIgnoreGlobally?.includes(error.StatusCode)) {
        return;
      }
      if (reasonsToIgnoreGlobally?.includes(error.Response?.Reason)) {
        return;
      }
      console.warn(error);

      if (isUnreachable(error)) {
        // meta.errorMessages do not apply to an unreachable server.
        // unreachableMutationMessageKey (errors.helpers.ts) picks the text:
        // "not saved" only when provable, a neutral text for a failed GET or a
        // mutation marked meta.savesNothing (sign-in), and otherwise a text
        // that asks the user to check before retrying.
        notifications.show({
          id: "server-unreachable",
          title: i18next.t("common.serverUnreachable.title"),
          message: i18next.t(
            unreachableMutationMessageKey(error, mutation.meta),
          ),
          classNames: errorNotificationClasses,
          color: "red.2",
          icon: xIcon,
          position: "bottom-center",
        });
        return;
      }

      const defaultErrorTitle = i18next.t(
        "common.notifications.errorMessages.somethingWentWrong",
      );
      let defaultErrorMessage = i18next.t(
        "common.notifications.errorMessages.pleaseTryAgain",
      );

      if (error instanceof NetworkError) {
        if (error.StatusCode === 403) {
          defaultErrorMessage = i18next.t(
            "common.notifications.errorMessages.missingPermissions",
          );
        }
      }

      notifications.show({
        title: errorMessage.title ?? defaultErrorTitle,
        message:
          typeof errorMessage.message === "function"
            ? errorMessage.message(error.Response)
            : (errorMessage.message ?? defaultErrorMessage),
        classNames: errorNotificationClasses,
        color: "red.2",
        icon: xIcon,
        position: "bottom-center",
      });
    },
  }),
});

// v8CssVariablesResolver keeps the transparent light variant colors from Mantine 8.
const resolver: CSSVariablesResolver = (theme) => {
  const v8 = v8CssVariablesResolver(theme);
  return {
    ...v8,
    variables: {
      ...v8.variables,
      "--mantine-color-dimmed": theme.colors.bluegray[5],
    },
  };
};

// The demo replaces fetch with its mock backend before the first query runs.
const ready = isDemo
  ? import("./demo").then((demo) => {
      demo.installDemo();
      // Demo student names follow the UI language, so reload what is shown.
      i18next.on("languageChanged", () => queryClient.invalidateQueries());
    })
  : Promise.resolve();

const rootElement = document.getElementById("root")!;
ready.then(() => {
  if (rootElement.innerHTML) return;
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <MantineProvider
          theme={SKILLSTER_THEME}
          cssVariablesResolver={resolver}
        >
          <Notifications
            position="top-center"
            zIndex={1000}
            pauseResetOnHover="notification"
          />
          <ErrorBoundary fallback={<AppCrashScreen />}>
            <ModalsProvider>
              <NavigationProgress />
              <Suspense fallback={<LoadingScreen />}>
                <AuthProvider>
                  <InnerApp />
                </AuthProvider>
              </Suspense>
            </ModalsProvider>
          </ErrorBoundary>
        </MantineProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});

function removeCountFromMessage(message?: string) {
  return message?.replace(/ \(\d+\)$/, "");
}

function updateMessageCount(message: string): string {
  const match = message.match(/ \((?<count>\d+)\)$/);
  if (match && match.groups?.count) {
    const currentCount = parseInt(match.groups.count, 10);
    return `${removeCountFromMessage(message)} (${currentCount + 1})`;
  } else {
    return `${message} (2)`;
  }
}
